import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Settings, Bell, Users, MapPin, Package, Clock, Shield, Database,
  Save, RefreshCw, AlertTriangle, CheckCircle2
} from 'lucide-react';

const WarehouseSettings = () => {
  const [settings, setSettings] = useState({
    notifications: {
      lowStockAlert: true,
      receivingComplete: true,
      pickingComplete: true,
      cycleCountDue: true,
      highUtilization: true,
    },
    operations: {
      autoAssignPutaway: true,
      requireQCApproval: true,
      enableFEFO: true,
      enableSlotting: true,
      scanToVerify: true,
    },
    inventory: {
      lowStockThreshold: 20,
      criticalStockThreshold: 10,
      enableLotTracking: true,
      enableSerialTracking: false,
      autoReorder: false,
    },
    performance: {
      targetPickRate: 140,
      targetAccuracy: 99,
      targetUtilization: 80,
      enableInterleaving: true,
    },
  });

  const [activeSection, setActiveSection] = useState('notifications');

  const handleSave = () => {
    // In production, this would save to backend
    alert('Settings saved successfully!');
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults?')) {
      // Reset logic
      alert('Settings reset to defaults');
    }
  };

  const sections = [
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'operations', label: 'Operations', icon: Package },
    { id: 'inventory', label: 'Inventory', icon: Database },
    { id: 'performance', label: 'Performance', icon: Clock },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Warehouse Settings</h2>
            <p className="text-sm text-gray-600 mt-1">Configure warehouse operations and preferences</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 text-sm"
            >
              <Save className="w-4 h-4" />
              Save Settings
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <nav className="space-y-2">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-orange-50 text-orange-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{section.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            {activeSection === 'notifications' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h3>
                {Object.entries(settings.notifications).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <label className="font-medium text-gray-900">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        {key === 'lowStockAlert' && 'Receive alerts when inventory falls below threshold'}
                        {key === 'receivingComplete' && 'Notify when receiving operations are completed'}
                        {key === 'pickingComplete' && 'Notify when picking operations are completed'}
                        {key === 'cycleCountDue' && 'Alert when cycle count tasks are due'}
                        {key === 'highUtilization' && 'Alert when warehouse utilization exceeds 90%'}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setSettings({
                          ...settings,
                          notifications: { ...settings.notifications, [key]: e.target.checked }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {activeSection === 'operations' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Operations Settings</h3>
                {Object.entries(settings.operations).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <label className="font-medium text-gray-900">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        {key === 'autoAssignPutaway' && 'Automatically assign optimal put-away locations'}
                        {key === 'requireQCApproval' && 'Require QC approval before put-away'}
                        {key === 'enableFEFO' && 'Enable First Expiry First Out logic'}
                        {key === 'enableSlotting' && 'Enable automatic slotting optimization'}
                        {key === 'scanToVerify' && 'Require barcode scan for verification'}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setSettings({
                          ...settings,
                          operations: { ...settings.operations, [key]: e.target.checked }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {activeSection === 'inventory' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Inventory Settings</h3>
                <div className="space-y-4">
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <label className="block font-medium text-gray-900 mb-2">Low Stock Threshold (%)</label>
                    <input
                      type="number"
                      value={settings.inventory.lowStockThreshold}
                      onChange={(e) => setSettings({
                        ...settings,
                        inventory: { ...settings.inventory, lowStockThreshold: parseInt(e.target.value) }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      min="0"
                      max="100"
                    />
                    <p className="text-sm text-gray-600 mt-1">Alert when inventory falls below this percentage</p>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <label className="block font-medium text-gray-900 mb-2">Critical Stock Threshold (%)</label>
                    <input
                      type="number"
                      value={settings.inventory.criticalStockThreshold}
                      onChange={(e) => setSettings({
                        ...settings,
                        inventory: { ...settings.inventory, criticalStockThreshold: parseInt(e.target.value) }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      min="0"
                      max="100"
                    />
                    <p className="text-sm text-gray-600 mt-1">Critical alert when inventory falls below this percentage</p>
                  </div>
                  {Object.entries(settings.inventory).filter(([key]) => typeof settings.inventory[key] === 'boolean').map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <label className="font-medium text-gray-900">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </label>
                        <p className="text-sm text-gray-600 mt-1">
                          {key === 'enableLotTracking' && 'Track items by lot numbers'}
                          {key === 'enableSerialTracking' && 'Track items by serial numbers'}
                          {key === 'autoReorder' && 'Automatically generate reorder requests'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) => setSettings({
                            ...settings,
                            inventory: { ...settings.inventory, [key]: e.target.checked }
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeSection === 'performance' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Targets</h3>
                <div className="space-y-4">
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <label className="block font-medium text-gray-900 mb-2">Target Pick Rate (items/hour)</label>
                    <input
                      type="number"
                      value={settings.performance.targetPickRate}
                      onChange={(e) => setSettings({
                        ...settings,
                        performance: { ...settings.performance, targetPickRate: parseInt(e.target.value) }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      min="0"
                    />
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <label className="block font-medium text-gray-900 mb-2">Target Accuracy (%)</label>
                    <input
                      type="number"
                      value={settings.performance.targetAccuracy}
                      onChange={(e) => setSettings({
                        ...settings,
                        performance: { ...settings.performance, targetAccuracy: parseInt(e.target.value) }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <label className="block font-medium text-gray-900 mb-2">Target Utilization (%)</label>
                    <input
                      type="number"
                      value={settings.performance.targetUtilization}
                      onChange={(e) => setSettings({
                        ...settings,
                        performance: { ...settings.performance, targetUtilization: parseInt(e.target.value) }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <label className="font-medium text-gray-900">Enable Task Interleaving</label>
                      <p className="text-sm text-gray-600 mt-1">Optimize worker routes by interleaving tasks</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.performance.enableInterleaving}
                        onChange={(e) => setSettings({
                          ...settings,
                          performance: { ...settings.performance, enableInterleaving: e.target.checked }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default WarehouseSettings;
